import { Icons } from "assets/icons";
import FilterBar from "components/common/FilterBar";
import NoDataFound from "components/common/NoDataFound";
import PageHeader from "components/common/PageHeader";
import Paginations from "components/common/Paginations";
import { ApiLogBadge } from "components/common/StatusBadge";
import DataTable from "components/common/table/DataTable";
import TableShimmer from "components/common/TableShimmer";
import { usePermission } from "hooks/usePermission";
import React, { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";

import {
  getAPILogsList,
  resetFilters,
  setFilter,
  getApiUrlDropdownlist,
} from "store/slices/apiLogsSlice";
import {
  API_LOGS_RESPONSE_OPTIONS,
  API_LOGS_STATUS_OPTION,
  ORDER_BY_OPTIONS,
} from "utils/constants";
import { formatAPIUrlOptions } from "utils/formatOptions";
import { getActionFlags } from "utils/getActionFlags";
import { formatUTCDate, getMinMaxDate } from "utils/utils";
import { usePageParam } from "hooks/usePageParam";

const APILogs = () => {
  const { t } = useTranslation();
  const { noPlans, view } = Icons || {};
  const dispatch = useDispatch();
  const [pageParam, setPageParam] = usePageParam();
  const { canAction } = usePermission();
  const { canView } = canAction("api_logs");

  const {
    items,
    loading,
    isLoading,
    loadingItemId,
    totalPages,
    itemsList: apiUrlsDropdownList,
    currentPage,
    totalCount,
    filters: { keyword, dates, status, orderBy, url, responseStatus },
  } = useSelector((state) => state.APILogs);

  useEffect(() => {
    if (canView) {
      dispatch(getApiUrlDropdownlist());
      getAllData(pageParam);
    }
  }, [canView]);
  const getAllData = (page, action) => {
    const { isReset, isResetKeyword } = getActionFlags(action);
    setPageParam(page || 1);

    const payload = {
      perPage: 50,
      pageNo: page || 1,
      status: isReset ? -1 : status,
      responseStatus: isReset ? 200 : responseStatus,
      orderBy: isReset ? -1 : orderBy,
      url: isReset ? "" : url,
      keyword: isResetKeyword ? "" : keyword || "",
      dateRange: isReset ? {} : getMinMaxDate(dates),
    };

    dispatch(getAPILogsList(payload));
  };

  const handleReset = () => {
    dispatch(resetFilters());
    getAllData(1, { actionType: "RESET" });
  };

  const filtersList = [
    {
      key: "status",
      placeholder: t("All"),
      type: "singleSelect",
      options: API_LOGS_STATUS_OPTION,
    },
    {
      key: "orderBy",
      placeholder: t("Order By"),
      type: "singleSelect",
      options: ORDER_BY_OPTIONS,
    },
    {
      key: "responseStatus",
      placeholder: t("All"),
      type: "singleSelect",
      options: API_LOGS_RESPONSE_OPTIONS,
    },
    {
      key: "url",
      placeholder: t("Select URL"),
      type: "singleSelect",
      options: formatAPIUrlOptions(apiUrlsDropdownList),
    },
  ];

  const columns = useMemo(
    () => [
      {
        header: t("Customer Name"),
        key: "userInfo",
        render: (value) =>
          value?.name?.trim() ? (
            <Link to={`/customers/customer-details?id=${value._id}`}>
              {value?.name}
            </Link>
          ) : (
            "-"
          ),
      },
      {
        header: t("Phone Number"),
        key: "userInfo",
        render: (value) =>
          value?.phone?.trim() ? (
            <Link to={`/customers/customer-details?id=${value._id}`}>
              {value?.phone}
            </Link>
          ) : (
            "-"
          ),
      },
      {
        header: t("Method"),
        key: "data",
        render: (value) => (
          <ApiLogBadge type="method" status={value?.method} t={t} />
        ),
      },
      {
        header: t("Path"),
        key: "data",
        render: (value) => (
          <div>
            <div>{value?.url}</div>
          </div>
        ),
      },
      {
        header: t("Status Code"),
        key: "data",
        render: (value) => (
          <ApiLogBadge type="result" status={value?.responseStatus} t={t} />
        ),
      },
      {
        header: t("Result"),
        key: "data",
        render: (value) => (
          <ApiLogBadge
            extra={value?.status}
            type="result"
            status={value?.status}
            t={t}
          />
        ),
      },
      {
        header: t("Duration"),
        key: "data",
        render: (value) => (
          <div>
            <div>{value?.duration}</div>
          </div>
        ),
      },
      {
        header: t("Created Date"),
        key: "createdAt",
        render: (value) => (
          <div className="ltr_text">{formatUTCDate(value)}</div>
        ),
      },
    ],
    [t],
  );

  const renderActions = useCallback(
    (item) => (
      <>
        {canView && (
          <Link to={`/api-logs/api-logs-details?id=${item._id}`}>
            <img src={view} width={20} height={20} alt="view" />
          </Link>
        )}
      </>
    ),
    [canView, isLoading, loadingItemId],
  );

  if (!canView) return null;

  return (
    <div className="page-content api_logs_page">
      <PageHeader title={t("API Logs")} />

      <FilterBar
        keyword={keyword}
        setKeyword={(value) => dispatch(setFilter({ key: "keyword", value }))}
        keywordPlaceholder={t("Search ")}
        filters={filtersList}
        selectedFilters={{ keyword, status, orderBy, url, responseStatus }}
        onFilterUpdate={(key, value) => dispatch(setFilter({ key, value }))}
        onSearch={getAllData}
        onReset={handleReset}
        isDateRange={true}
        dates={dates}
        setDates={(value) => dispatch(setFilter({ key: "dates", value }))}
      />

      {loading && <TableShimmer rows={12} columns={10} />}

      {!loading && items?.length === 0 && (
        <NoDataFound title={t("No API Logs Found")} icon={noPlans} />
      )}
      {!loading && items?.length > 0 && (
        <>
          <DataTable
            columns={columns}
            data={items}
            renderActions={renderActions}
            t={t}
          />

          <Paginations
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            onPageChange={getAllData}
          />
        </>
      )}
    </div>
  );
};

export default APILogs;
